import { Link } from "react-router-dom";
import { Wrench, ArrowLeft } from "lucide-react";

function Section({ n, title, children }) {
  return (
    <section className="mt-7">
      <h2 className="font-display text-lg font-bold uppercase tracking-wide text-midnight">{n}. {title}</h2>
      <div className="mt-2 space-y-2 text-sm leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}

function Bullets({ items }) {
  return (
    <ul className="ml-5 list-disc space-y-1">
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      <header className="flex items-center gap-2.5 bg-midnight px-6 py-4">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/10"><Wrench className="h-4 w-4 text-brand" /></div>
          <span className="font-display text-[15px] font-bold tracking-widest text-white">PRO GARAGES</span>
        </Link>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-10">
        <h1 className="font-display text-3xl font-bold uppercase text-midnight">Terms of Service</h1>
        <p className="mt-1 text-xs text-muted-foreground">Last updated: 12/10/2026</p>

        <div className="mt-6 rounded-xl border border-border bg-card p-6">
          <Section n={1} title="About these terms">
            <p>These Terms of Service govern your access to and use of Pro Garages, an online garage-management software service.</p>
            <p>Pro Garages is operated by BIS AUTO BODYWORKS LTD, trading as Pro Garages, of Unit 7 Kartar Trading Estate, Shale Street, Bilston, United Kingdom, WV14 0HF.</p>
            <p>Company number: 17193494</p>
            <p>Contact email: <a className="font-semibold text-brand-2" href="mailto:support@progarages.co.uk">support@progarages.co.uk</a></p>
            <p>By creating an account or purchasing a subscription, you agree to the terms that apply to your use of the service. Please save a copy for your records.</p>
          </Section>

          <Section n={2} title="What Pro Garages provides">
            <p>Pro Garages provides software intended to help garages manage their day-to-day business. Depending on the plan and features available, this may include:</p>
            <Bullets items={[
              "Customer and vehicle records.",
              "Jobs, bookings and calendars.",
              "Quotations and estimates.",
              "Invoices and payment records.",
              "Parts, inventory and suppliers.",
              "Staff and team management.",
              "Vehicle registration and MOT-related lookups.",
              "Documents, reports and customer communications.",
            ]} />
            <p>The available features and any plan-specific restrictions will be described in the app or on the relevant pricing page.</p>
          </Section>

          <Section n={3} title="Creating and securing an account">
            <p>You must provide accurate information when registering and keep your account details up to date.</p>
            <p>You are responsible for protecting your password, keeping access to your email account secure and ensuring that only authorised people use your account. Where team accounts and individual user permissions are available, each person should use their own authorised login.</p>
            <p>You must notify us promptly if you suspect unauthorised access to your account.</p>
            <p>We may require email verification before allowing access to protected features. We may restrict access where reasonably necessary to protect the service, its users or their data.</p>
          </Section>

          <Section n={4} title="Subscriptions and prices">
            <p>The stated subscription prices are:</p>
            <Bullets items={[
              "Basic: £40 per month or £400 per year.",
              "Pro: £50 per month or £500 per year.",
              "Premium: £60 per month or £600 per year.",
            ]} />
            <p>The available payment interval, features and price for your selected plan will be displayed before you subscribe.</p>
            <p>The business has stated that it is not VAT registered. The prices must be updated if its VAT status changes.</p>
            <p>Payments are processed through the payment provider presented during checkout. Your subscription confirmation should state the plan purchased, billing interval, applicable price and relevant renewal details.</p>
          </Section>

          <Section n={5} title="Free trial">
            <p>A 14-day trial is offered where advertised.</p>
            <p>Before registering or entering payment details, you should be told:</p>
            <Bullets items={[
              "When the trial begins and ends.",
              "Whether payment details are required.",
              "Whether the subscription automatically changes to a paid subscription.",
              "The amount and date of the first payment.",
              "How to cancel before the trial ends.",
            ]} />
            <p>The actual trial and billing arrangements displayed at checkout form part of your agreement with us. We must ensure that the website, app and payment system are consistent.</p>
          </Section>

          <Section n={6} title="Renewals, payments and failed payments">
            <p>Unless the checkout states otherwise, subscriptions renew at the selected billing interval until cancelled.</p>
            <p>If a payment fails, we may contact you to ask you to resolve the problem. Where reasonably necessary, and subject to applicable law and the agreed subscription terms, we may restrict paid features if payment remains outstanding.</p>
            <p>We will not intentionally charge a different subscription price from the one properly agreed without following the applicable contractual and legal requirements.</p>
          </Section>

          <Section n={7} title="Cancellation and refunds">
            <p>You may cancel your subscription using the cancellation method made available in your account or by contacting <a className="font-semibold text-brand-2" href="mailto:support@progarages.co.uk">support@progarages.co.uk</a>.</p>
            <p>The exact cancellation method and the date on which access ends must be clearly explained in your account. We should confirm receipt of cancellation requests.</p>
            <p>Cancellation will normally prevent future renewals. Whether you retain access until the end of a paid period, and whether a refund or credit is available, will depend on the subscription agreement and applicable law.</p>
            <p>Nothing in these terms removes any mandatory statutory rights, including consumer cancellation rights where they apply.</p>
            <p>The detailed cancellation, trial and refund rules will be set out in our Subscription, Trial, Cancellation and Refund Policy.</p>
          </Section>

          <Section n={8} title="Your business information and data">
            <p>You retain your rights in the business information and content you enter into Pro Garages.</p>
            <p>You authorise us to host, store, process and display that information as reasonably necessary to provide, secure and support the service and to comply with applicable law.</p>
            <p>You are responsible for ensuring that you have the necessary rights and lawful basis to enter personal information relating to your customers, employees, suppliers and other individuals.</p>
            <p>You must provide appropriate privacy information to people whose information you collect and use, and respond to requests relating to information for which your business is responsible.</p>
            <p>Our Privacy Policy and Data Processing Agreement explain how responsibilities are divided between Pro Garages and its customers.</p>
          </Section>

          <Section n={9} title="Acceptable use">
            <p>You must not:</p>
            <Bullets items={[
              "Use the service for unlawful purposes.",
              "Access another customer's account or data without permission.",
              "Attempt to bypass account permissions or security controls.",
              "Upload malicious software or deliberately disrupt the service.",
              "Use email features for unlawful communications.",
              "Upload information you have no right to use.",
              "Misrepresent vehicle lookup results or other third-party information.",
            ]} />
            <p>Further requirements appear in our Acceptable Use Policy.</p>
          </Section>

          <Section n={10} title="Availability and maintenance">
            <p>We will take reasonable steps to maintain the service. However, uninterrupted availability cannot be guaranteed.</p>
            <p>Access may occasionally be affected by maintenance, technical problems, internet connectivity, security measures or third-party service interruptions.</p>
            <p>Unless expressly agreed in a separate service-level agreement, no guaranteed uptime percentage is promised.</p>
          </Section>

          <Section n={11} title="Third-party services">
            <p>Certain features may depend on external providers, including payment processing, email delivery, hosting or vehicle-data lookup services.</p>
            <p>Those services may have their own terms and availability limitations. We will identify relevant providers in our Third-Party Services and Subprocessors List as appropriate.</p>
            <p>A vehicle lookup should not be treated as an official confirmation from a government authority unless the service expressly establishes that status.</p>
          </Section>

          <Section n={12} title="Intellectual property">
            <p>Pro Garages and its licensors retain their rights in the software, branding, design and materials that make up the service, except for customer-owned content and third-party materials.</p>
            <p>While your subscription is active, we grant you a limited, non-exclusive and non-transferable right to use the service for your authorised business purposes, subject to these terms.</p>
            <p>You must not copy, resell or exploit the service beyond what is permitted by law or expressly authorised by us.</p>
          </Section>

          <Section n={13} title="Suspension and termination">
            <p>We may suspend or restrict access where reasonably necessary to address a security risk, suspected fraud, unlawful activity, serious misuse or a material breach of these terms.</p>
            <p>Where practical and lawful, we will explain the reason and give you an opportunity to resolve the issue.</p>
            <p>Subscription cancellation, account closure, data export and deletion are separate matters. Cancelling a subscription must not be assumed to delete your records, and deleting an account must not be assumed to cancel a payment-provider subscription unless the relevant process confirms this.</p>
            <p>Our Data Export, Retention and Deletion Policy explains how account data is handled when the service ends.</p>
          </Section>

          <Section n={14} title="Liability and legal rights">
            <p>We will comply with applicable law and will not exclude or limit liability where the law does not allow us to do so.</p>
            <p>Any contractual limits on liability, exclusions of particular losses and allocation of business risks must be stated clearly and reviewed for enforceability, including where a customer may be a consumer.</p>
          </Section>

          <Section n={15} title="Changes to the service or terms">
            <p>We may update the service and these terms as the product develops, for security, operational, legal or other legitimate reasons.</p>
            <p>For material changes to contractual terms, we will provide appropriate notice and explain any choices available to you where required.</p>
            <p>The current version and its effective date should be available on our website or in the app.</p>
          </Section>

          <Section n={16} title="Complaints and contact">
            <p>If you have a question or complaint, contact:</p>
            <p>Email: <a className="font-semibold text-brand-2" href="mailto:support@progarages.co.uk">support@progarages.co.uk</a></p>
            <p>Postal address: BIS AUTO BODYWORKS LTD, 17193494. Unit 7 Kartar Trading Estate, Shale Street, Bilston, United Kingdom, WV14 0HF.</p>
          </Section>
        </div>

        <Link to="/" data-testid="terms-back-home" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Back home</Link>
      </div>
    </div>
  );
}
